import React, { useMemo, useState } from 'react';
import { useData } from '../../store/DataContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Plus, Trash2, Search, Save, Send } from 'lucide-react';
import { BillingSection } from './BillingModule';
import { InvoiceItem, Invoice } from '../../types';
interface Props {
  onNavigate: (section: BillingSection) => void;
}
export const NewInvoice: React.FC<Props> = ({
  onNavigate
}) => {
  const {
    patients,
    walkInClients,
    billingItems,
    addInvoice,
    currentUser,
    invoices,
    getPatientEncounters,
    logBillingAudit
  } = useData();
  const [customerType, setCustomerType] = useState<'Patient' | 'WalkIn'>('Patient');
  const [customerId, setCustomerId] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [dueDate, setDueDate] = useState(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState<InvoiceItem[]>([]);
  const [showItemSearch, setShowItemSearch] = useState(false);
  const [itemSearchQuery, setItemSearchQuery] = useState('');
  // Generate next invoice number
  const nextInvoiceNumber = useMemo(() => {
    const prefix = 'INV-';
    const maxNum = invoices.reduce((max, inv) => {
      if (inv.invoiceNumber.startsWith(prefix)) {
        const num = parseInt(inv.invoiceNumber.slice(prefix.length), 10);
        return !isNaN(num) && num > max ? num : max;
      }
      return max;
    }, 0);
    return `${prefix}${(maxNum + 1).toString().padStart(5, '0')}`;
  }, [invoices]);
  const activeBillingItems = billingItems.filter(i => i.status === 'Active');
  const filteredSearchItems = activeBillingItems.filter(i => i.serviceName.toLowerCase().includes(itemSearchQuery.toLowerCase()) || i.itemCode.toLowerCase().includes(itemSearchQuery.toLowerCase()));
  const handleAddItem = (billingItem: (typeof billingItems)[0]) => {
    const newItem: InvoiceItem = {
      id: `inv-item-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
      itemId: billingItem.id,
      serviceName: billingItem.serviceName,
      description: billingItem.description || '',
      quantity: 1,
      unitPrice: billingItem.unitPrice,
      discount: 0,
      tax: billingItem.taxRate,
      total: 0 // Calculated below
    };
    // Calculate initial total
    const sub = newItem.quantity * newItem.unitPrice;
    const discAmt = sub * (newItem.discount / 100);
    const taxAmt = (sub - discAmt) * (newItem.tax / 100);
    newItem.total = sub - discAmt + taxAmt;
    setItems([...items, newItem]);
    setShowItemSearch(false);
    setItemSearchQuery('');
  };
  const handleUpdateItem = (id: string, field: keyof InvoiceItem, value: any) => {
    setItems(items.map(item => {
      if (item.id === id) {
        const updated = {
          ...item,
          [field]: value
        };
        // Recalculate total
        const sub = updated.quantity * updated.unitPrice;
        const discAmt = sub * (updated.discount / 100);
        const taxAmt = (sub - discAmt) * (updated.tax / 100);
        updated.total = sub - discAmt + taxAmt;
        return updated;
      }
      return item;
    }));
  };
  const handleRemoveItem = (id: string) => {
    setItems(items.filter(i => i.id !== id));
  };
  const handleAutoFill = () => {
    if (!customerId || customerType !== 'Patient') return;
    const encounters = getPatientEncounters(customerId);
    const activeEncounter = encounters.find(e => e.stage !== 'COMPLETED');
    if (!activeEncounter) {
      alert('No active encounter found for this patient.');
      return;
    }
    const newItems: InvoiceItem[] = [];
    // Add Consultation
    const consItem = billingItems.find(i => i.category === 'Consultation');
    if (consItem) {
      newItems.push({
        id: `inv-item-${Date.now()}-cons`,
        itemId: consItem.id,
        serviceName: consItem.serviceName,
        description: 'General Consultation',
        quantity: 1,
        unitPrice: consItem.unitPrice,
        discount: 0,
        tax: consItem.taxRate,
        total: consItem.unitPrice + consItem.unitPrice * (consItem.taxRate / 100)
      });
    }
    // Add Lab Tests
    activeEncounter.labRequests.forEach(lab => {
      lab.tests.forEach(test => {
        const labItem = billingItems.find(i => i.category === 'Laboratory' && (i.serviceName.includes(test) || test.includes(i.serviceName)));
        if (labItem) {
          newItems.push({
            id: `inv-item-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
            itemId: labItem.id,
            serviceName: labItem.serviceName,
            description: `Lab Test: ${test}`,
            quantity: 1,
            unitPrice: labItem.unitPrice,
            discount: 0,
            tax: labItem.taxRate,
            total: labItem.unitPrice + labItem.unitPrice * (labItem.taxRate / 100)
          });
        }
      });
    });
    // Add Radiology
    activeEncounter.radiologyRequests.forEach(rad => {
      const radItem = billingItems.find(i => i.category === 'Radiology' && (i.serviceName.includes(rad.scanType) || rad.scanType.includes(i.serviceName)));
      if (radItem) {
        newItems.push({
          id: `inv-item-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
          itemId: radItem.id,
          serviceName: radItem.serviceName,
          description: `Scan: ${rad.scanType}`,
          quantity: 1,
          unitPrice: radItem.unitPrice,
          discount: 0,
          tax: radItem.taxRate,
          total: radItem.unitPrice + radItem.unitPrice * (radItem.taxRate / 100)
        });
      }
    });
    // Add Pharmacy
    activeEncounter.prescriptions.forEach(rx => {
      const rxItem = billingItems.find(i => i.category === 'Pharmacy' && (i.serviceName.includes(rx.drugName) || rx.drugName.includes(i.serviceName)));
      if (rxItem) {
        newItems.push({
          id: `inv-item-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
          itemId: rxItem.id,
          serviceName: rxItem.serviceName,
          description: `Prescription: ${rx.drugName}`,
          quantity: 1,
          unitPrice: rxItem.unitPrice,
          discount: 0,
          tax: rxItem.taxRate,
          total: rxItem.unitPrice + rxItem.unitPrice * (rxItem.taxRate / 100)
        });
      }
    });
    if (newItems.length > 0) {
      setItems([...items, ...newItems]);
    } else {
      alert('No matching billable items found for the active encounter. Please ensure your Item List is populated with matching service names.');
    }
  };
  // Totals calculation
  const totals = useMemo(() => {
    let subtotal = 0;
    let totalDiscount = 0;
    let totalTax = 0;
    let totalAmount = 0;
    items.forEach(item => {
      const sub = item.quantity * item.unitPrice;
      const discAmt = sub * (item.discount / 100);
      const taxAmt = (sub - discAmt) * (item.tax / 100);
      subtotal += sub;
      totalDiscount += discAmt;
      totalTax += taxAmt;
      totalAmount += item.total;
    });
    return {
      subtotal,
      totalDiscount,
      totalTax,
      totalAmount
    };
  }, [items]);
  const handleSave = (status: 'Draft' | 'Sent') => {
    if (!customerId) {
      alert('Please select a customer.');
      return;
    }
    if (items.length === 0) {
      alert('Please add at least one item to the invoice.');
      return;
    }
    const payload: Omit<Invoice, 'id'> = {
      invoiceNumber: nextInvoiceNumber,
      customerId,
      customerType,
      date,
      dueDate,
      salesperson: currentUser?.id || '',
      notes,
      status,
      items,
      subtotal: totals.subtotal,
      totalDiscount: totals.totalDiscount,
      totalTax: totals.totalTax,
      totalAmount: totals.totalAmount,
      amountPaid: 0,
      balanceDue: totals.totalAmount,
      createdAt: new Date().toISOString(),
      createdBy: currentUser?.id || ''
    };
    addInvoice(payload);
    logBillingAudit('Create Invoice', `Created invoice ${payload.invoiceNumber} for ${formatCurrency(payload.totalAmount)}`);
    onNavigate(status === 'Draft' ? 'invoices-draft' : 'invoices-sent');
  };
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-ZM', {
      style: 'currency',
      currency: 'ZMW'
    }).format(amount);
  };
  return <div className="space-y-6 max-w-5xl mx-auto pb-20" data-id="element-1248">
      <div className="flex justify-between items-center" data-id="element-1249">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-1250">New Invoice</h1>
        <div className="text-xl font-mono text-gray-500" data-id="element-1251">
          {nextInvoiceNumber}
        </div>
      </div>

      <Card data-id="element-1252">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1253">
          <div className="space-y-4" data-id="element-1254">
            <Select label="Customer Type" value={customerType} onChange={e => {
            setCustomerType(e.target.value as any);
            setCustomerId('');
          }} options={[{
            value: 'Patient',
            label: 'Registered Patient'
          }, {
            value: 'WalkIn',
            label: 'Walk-in Client'
          }]} data-id="element-1255" />

            {customerType === 'Patient' ? <Select label="Select Patient" value={customerId} onChange={e => setCustomerId(e.target.value)} options={[{
            value: '',
            label: '-- Select Patient --'
          }, ...patients.map(p => ({
            value: p.id,
            label: `${p.bmcFileNumber} - ${p.firstName} ${p.lastName}`
          }))]} data-id="element-1256" /> : <Select label="Select Walk-in Client" value={customerId} onChange={e => setCustomerId(e.target.value)} options={[{
            value: '',
            label: '-- Select Client --'
          }, ...walkInClients.map(c => ({
            value: c.id,
            label: `${c.name} ${c.phone ? `(${c.phone})` : ''}`
          }))]} data-id="element-1257" />}
          </div>

          <div className="space-y-4" data-id="element-1258">
            <Input label="Invoice Date" type="date" value={date} onChange={e => setDate(e.target.value)} data-id="element-1259" />
            <Input label="Due Date" type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} data-id="element-1260" />
          </div>
        </div>
      </Card>

      <Card noPadding data-id="element-1261">
        <div className="p-4 border-b border-gray-200 bg-gray-50 flex justify-between items-center" data-id="element-1262">
          <h3 className="font-bold text-gray-700" data-id="element-1263">Invoice Items</h3>
          <div className="flex space-x-2" data-id="element-1264">
            {customerType === 'Patient' && customerId && <Button size="sm" variant="outline" onClick={handleAutoFill} data-id="element-1265">
                Auto-fill from Encounter
              </Button>}
            <Button size="sm" onClick={() => setShowItemSearch(true)} data-id="element-1266">
              <Plus className="w-4 h-4 mr-2" data-id="element-1267" /> Add Item
            </Button>
          </div>
        </div>

        {showItemSearch && <div className="p-4 border-b border-gray-200 bg-blue-50" data-id="element-1268">
            <div className="relative max-w-md mb-2" data-id="element-1269">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1270">
                <Search className="h-4 w-4 text-gray-400" data-id="element-1271" />
              </div>
              <input type="text" autoFocus className="block w-full pl-10 pr-3 py-2 border border-blue-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="Search service catalogue..." value={itemSearchQuery} onChange={e => setItemSearchQuery(e.target.value)} data-id="element-1272" />
            </div>
            {itemSearchQuery && <div className="bg-white border border-gray-200 rounded-md shadow-sm max-h-48 overflow-y-auto" data-id="element-1273">
                {filteredSearchItems.length > 0 ? <ul className="divide-y divide-gray-100" data-id="element-1274">
                    {filteredSearchItems.map(item => <li key={item.id} className="p-2 hover:bg-blue-50 cursor-pointer flex justify-between items-center" onClick={() => handleAddItem(item)} data-id="element-1275">
                        <div data-id="element-1276">
                          <p className="text-sm font-medium text-gray-900" data-id="element-1277">
                            {item.serviceName}
                          </p>
                          <p className="text-xs text-gray-500" data-id="element-1278">
                            {item.itemCode} • {item.category}
                          </p>
                        </div>
                        <span className="text-sm font-bold text-gray-900" data-id="element-1279">
                          {formatCurrency(item.unitPrice)}
                        </span>
                      </li>)}
                  </ul> : <div className="p-3 text-sm text-gray-500 text-center" data-id="element-1280">
                    No items found.
                  </div>}
              </div>}
            <div className="mt-2 text-right" data-id="element-1281">
              <button type="button" className="text-sm text-blue-600 hover:text-blue-800" onClick={() => {
            setShowItemSearch(false);
            setItemSearchQuery('');
          }} data-id="element-1282">
                Close Search
              </button>
            </div>
          </div>}

        <div className="overflow-x-auto" data-id="element-1283">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-1284">
            <thead className="bg-gray-50" data-id="element-1285">
              <tr data-id="element-1286">
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/3" data-id="element-1287">
                  Item Details
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-24" data-id="element-1288">
                  Qty
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-32" data-id="element-1289">
                  Rate
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-24" data-id="element-1290">
                  Disc(%)
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-24" data-id="element-1291">
                  Tax(%)
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-32" data-id="element-1292">
                  Amount
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-16" data-id="element-1293"></th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-1294">
              {items.length > 0 ? items.map(item => <tr key={item.id} data-id="element-1295">
                    <td className="px-4 py-3" data-id="element-1296">
                      <input type="text" className="w-full border-0 border-b border-transparent hover:border-gray-300 focus:border-blue-500 focus:ring-0 p-1 text-sm font-medium text-gray-900" value={item.serviceName} onChange={e => handleUpdateItem(item.id, 'serviceName', e.target.value)} data-id="element-1297" />
                      <input type="text" className="w-full border-0 border-b border-transparent hover:border-gray-300 focus:border-blue-500 focus:ring-0 p-1 text-xs text-gray-500 mt-1" placeholder="Description (optional)" value={item.description} onChange={e => handleUpdateItem(item.id, 'description', e.target.value)} data-id="element-1298" />
                    </td>
                    <td className="px-4 py-3" data-id="element-1299">
                      <input type="number" min="1" className="w-full border border-gray-300 rounded p-1 text-sm text-right focus:ring-blue-500 focus:border-blue-500" value={item.quantity} onChange={e => handleUpdateItem(item.id, 'quantity', parseFloat(e.target.value) || 0)} data-id="element-1300" />
                    </td>
                    <td className="px-4 py-3" data-id="element-1301">
                      <input type="number" min="0" step="0.01" className="w-full border border-gray-300 rounded p-1 text-sm text-right focus:ring-blue-500 focus:border-blue-500" value={item.unitPrice} onChange={e => handleUpdateItem(item.id, 'unitPrice', parseFloat(e.target.value) || 0)} data-id="element-1302" />
                    </td>
                    <td className="px-4 py-3" data-id="element-1303">
                      <input type="number" min="0" max="100" className="w-full border border-gray-300 rounded p-1 text-sm text-right focus:ring-blue-500 focus:border-blue-500" value={item.discount} onChange={e => handleUpdateItem(item.id, 'discount', parseFloat(e.target.value) || 0)} data-id="element-1304" />
                    </td>
                    <td className="px-4 py-3" data-id="element-1305">
                      <input type="number" min="0" max="100" className="w-full border border-gray-300 rounded p-1 text-sm text-right focus:ring-blue-500 focus:border-blue-500" value={item.tax} onChange={e => handleUpdateItem(item.id, 'tax', parseFloat(e.target.value) || 0)} data-id="element-1306" />
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-medium text-gray-900" data-id="element-1307">
                      {formatCurrency(item.total)}
                    </td>
                    <td className="px-4 py-3 text-center" data-id="element-1308">
                      <button onClick={() => handleRemoveItem(item.id)} className="text-gray-400 hover:text-red-600" data-id="element-1309">
                        <Trash2 className="w-4 h-4" data-id="element-1310" />
                      </button>
                    </td>
                  </tr>) : <tr data-id="element-1311">
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-500 text-sm" data-id="element-1312">
                    No items added yet. Click "Add Item" to search the
                    catalogue.
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>

        <div className="p-6 bg-gray-50 border-t border-gray-200 flex flex-col md:flex-row justify-between" data-id="element-1313">
          <div className="w-full md:w-1/2 mb-4 md:mb-0 pr-4" data-id="element-1314">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1315">
              Customer Notes
            </label>
            <textarea className="w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm" rows={4} placeholder="Notes, terms, or payment instructions..." value={notes} onChange={e => setNotes(e.target.value)} data-id="element-1316" />
          </div>
          <div className="w-full md:w-1/3 space-y-3" data-id="element-1317">
            <div className="flex justify-between text-sm" data-id="element-1318">
              <span className="text-gray-600" data-id="element-1319">Subtotal</span>
              <span className="font-medium" data-id="element-1320">
                {formatCurrency(totals.subtotal)}
              </span>
            </div>
            <div className="flex justify-between text-sm" data-id="element-1321">
              <span className="text-gray-600" data-id="element-1322">Discount</span>
              <span className="font-medium text-red-600" data-id="element-1323">
                -{formatCurrency(totals.totalDiscount)}
              </span>
            </div>
            <div className="flex justify-between text-sm" data-id="element-1324">
              <span className="text-gray-600" data-id="element-1325">Tax</span>
              <span className="font-medium" data-id="element-1326">
                {formatCurrency(totals.totalTax)}
              </span>
            </div>
            <div className="flex justify-between text-lg font-bold border-t border-gray-300 pt-2 mt-2" data-id="element-1327">
              <span className="text-gray-900" data-id="element-1328">Total</span>
              <span className="text-blue-700" data-id="element-1329">
                {formatCurrency(totals.totalAmount)}
              </span>
            </div>
          </div>
        </div>
      </Card>

      <div className="fixed bottom-0 left-0 right-0 md:left-64 bg-white border-t border-gray-200 p-4 flex justify-end space-x-4 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] z-10" data-id="element-1330">
        <Button variant="outline" onClick={() => handleSave('Draft')} data-id="element-1331">
          <Save className="w-4 h-4 mr-2" data-id="element-1332" /> Save as Draft
        </Button>
        <Button onClick={() => handleSave('Sent')} data-id="element-1333">
          <Send className="w-4 h-4 mr-2" data-id="element-1334" /> Save & Send
        </Button>
      </div>
    </div>;
};